import { useState } from "react";
import { Link } from "react-router";
import { Button, Card, CardContent } from "@bookdental/ui";
import { trpc } from "@/lib/trpc";
import { ErroConsulta } from "@/components/ErroConsulta";

const POR_PAGINA = 50;

/** O que os admins da plataforma fizeram — quem, quando, em quem e com que detalhe. */
export function AdminAuditoria() {
  const [acao, setAcao] = useState("");
  const [pagina, setPagina] = useState(0);
  const { data, isLoading, error } = trpc.admin.auditoria.list.useQuery({
    acao: acao || undefined,
    take: POR_PAGINA,
    skip: pagina * POR_PAGINA,
  });
  const total = data?.total ?? 0;
  const paginas = Math.max(1, Math.ceil(total / POR_PAGINA));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-admin-900">Auditoria</h1>
        <p className="mt-1 text-sm text-admin-700">Tudo o que os admins fizeram em contas, organizações e planos.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select
          value={acao}
          onChange={(e) => {
            setAcao(e.target.value);
            setPagina(0);
          }}
          className="h-9 rounded-md border border-admin-200 bg-white px-2 text-sm"
          aria-label="Filtrar por ação"
        >
          <option value="">Todas as ações</option>
          {(data?.acoes ?? []).map((a) => (
            <option key={a.chave} value={a.chave}>{a.rotulo}</option>
          ))}
        </select>
        <span className="text-sm text-admin-700">{isLoading ? "..." : `${total} registro${total === 1 ? "" : "s"}`}</span>
      </div>

      <Card className="border-admin-200">
        <CardContent className="overflow-x-auto p-0">
          {error ? (
            <div className="p-5"><ErroConsulta error={error} /></div>
          ) : (
            <table className="w-full text-sm">
              <thead className="border-b border-admin-200 text-left text-xs uppercase tracking-wide text-admin-700">
                <tr>
                  <th className="px-4 py-2.5">Quando</th>
                  <th className="px-4 py-2.5">Admin</th>
                  <th className="px-4 py-2.5">Ação</th>
                  <th className="px-4 py-2.5">Conta</th>
                  <th className="px-4 py-2.5">Detalhes</th>
                </tr>
              </thead>
              <tbody>
                {(data?.itens ?? []).map((i) => (
                  <tr key={i.id} className="border-b border-zinc-100 align-top last:border-0">
                    <td className="whitespace-nowrap px-4 py-2.5 text-admin-700">{new Date(i.createdAt).toLocaleString("pt-BR")}</td>
                    <td className="px-4 py-2.5">{i.actor?.name ?? i.actor?.email}</td>
                    <td className="px-4 py-2.5 font-medium text-admin-900">{i.rotulo}</td>
                    <td className="px-4 py-2.5">
                      {i.target ? <Link to={`/usuarios/${i.target.id}`} className="underline">{i.target.email}</Link> : "—"}
                    </td>
                    <td className="max-w-xs px-4 py-2.5 font-mono text-xs text-admin-700">
                      {i.details ? Object.entries(i.details as Record<string, unknown>).map(([k, v]) => `${k}: ${String(v)}`).join(" · ") : ""}
                    </td>
                  </tr>
                ))}
                {!isLoading && data?.itens.length === 0 && (
                  <tr><td colSpan={5} className="px-4 py-10 text-center text-admin-700">Nada registrado ainda.</td></tr>
                )}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      {paginas > 1 && (
        <div className="flex items-center justify-end gap-2">
          <Button size="sm" variant="outline" disabled={pagina === 0} onClick={() => setPagina((p) => p - 1)}>Anterior</Button>
          <span className="text-sm text-admin-700">{pagina + 1} de {paginas}</span>
          <Button size="sm" variant="outline" disabled={pagina + 1 >= paginas} onClick={() => setPagina((p) => p + 1)}>Próxima</Button>
        </div>
      )}
    </div>
  );
}
